import { compareFinancialRecordsByDate, getFinancialDate, isValidCivilDate } from './dates';
import {
  FIRESTORE_FINANCIAL_SCHEMA_VERSION,
  FIRESTORE_TRANSACTION_STATUS,
  FIRESTORE_TRANSACTION_TYPES,
  toFirestoreStatus,
  toFirestoreType,
} from './firestoreSchema';
import { getAmountCents } from './money';
import { generateRecurrenceOccurrences } from './recurrence';
import {
  RECURRING_OCCURRENCE_STATE,
  RECURRING_SEGMENT_STATUS,
  fromFirestoreRecurringOccurrenceState,
  fromFirestoreRecurringSegment,
  toDomainRecurrenceRule,
} from './recurrenceFirestoreSchema';

export const FINANCIAL_OCCURRENCE_SOURCE = Object.freeze({
  TRANSACTION: 'transaction',
  RECURRING: 'recurring',
});

export const FINANCIAL_OCCURRENCE_ERROR_CODES = Object.freeze({
  INVALID_INPUT: 'INVALID_FINANCIAL_OCCURRENCE_INPUT',
  INVALID_RANGE: 'INVALID_FINANCIAL_OCCURRENCE_RANGE',
  INVALID_TRANSACTION: 'INVALID_FINANCIAL_OCCURRENCE_TRANSACTION',
  INVALID_SEGMENT: 'INVALID_FINANCIAL_OCCURRENCE_SEGMENT',
  INVALID_STATE: 'INVALID_FINANCIAL_OCCURRENCE_STATE',
  DUPLICATE_TRANSACTION: 'DUPLICATE_FINANCIAL_OCCURRENCE_TRANSACTION',
  DUPLICATE_VIRTUAL: 'DUPLICATE_FINANCIAL_OCCURRENCE_VIRTUAL',
  DUPLICATE_STATE: 'DUPLICATE_FINANCIAL_OCCURRENCE_STATE',
  STATE_SEGMENT_MISSING: 'FINANCIAL_OCCURRENCE_STATE_SEGMENT_MISSING',
  STATE_SEGMENT_MISMATCH: 'FINANCIAL_OCCURRENCE_STATE_SEGMENT_MISMATCH',
  STATE_NOT_IN_SCHEDULE: 'FINANCIAL_OCCURRENCE_STATE_NOT_IN_SCHEDULE',
  MATERIALIZED_TRANSACTION_MISSING: 'FINANCIAL_OCCURRENCE_MATERIALIZED_TRANSACTION_MISSING',
  MATERIALIZED_STATE_MISSING: 'FINANCIAL_OCCURRENCE_MATERIALIZED_STATE_MISSING',
  MATERIALIZED_TRANSACTION_MISMATCH: 'FINANCIAL_OCCURRENCE_MATERIALIZED_TRANSACTION_MISMATCH',
  TRANSACTION_LINKED_TWICE: 'FINANCIAL_OCCURRENCE_TRANSACTION_LINKED_TWICE',
  SKIPPED_TRANSACTION_PRESENT: 'FINANCIAL_OCCURRENCE_SKIPPED_TRANSACTION_PRESENT',
});

export class FinancialOccurrenceResolutionError extends Error {
  constructor(code, details = [], cause = null) {
    super(code);
    this.name = 'FinancialOccurrenceResolutionError';
    this.code = code;
    this.details = details;
    this.cause = cause;
  }
}

const fail = (code, details = [], cause = null) => {
  throw new FinancialOccurrenceResolutionError(code, details, cause);
};

const normalizeTransactionType = (transaction) => {
  if (Object.values(FIRESTORE_TRANSACTION_TYPES).includes(transaction?.type)) return transaction.type;
  if (Object.values(FIRESTORE_TRANSACTION_TYPES).includes(transaction?.sourceType)) return transaction.sourceType;
  return toFirestoreType(transaction?.tipo);
};

const normalizeTransactionStatus = (transaction) => {
  if (Object.values(FIRESTORE_TRANSACTION_STATUS).includes(transaction?.status)) return transaction.status;
  if (Object.values(FIRESTORE_TRANSACTION_STATUS).includes(transaction?.sourceStatus)) {
    return transaction.sourceStatus;
  }
  return toFirestoreStatus(transaction?.status);
};

const normalizePersistedTransaction = (transaction, index) => {
  const id = transaction?.id;
  const type = normalizeTransactionType(transaction);
  const status = normalizeTransactionStatus(transaction);
  const amountCents = getAmountCents(transaction);
  const date = getFinancialDate(transaction);
  const yearMonth = transaction?.yearMonth;
  const name = typeof transaction?.name === 'string' ? transaction.name : transaction?.nome;
  const category = typeof transaction?.category === 'string'
    ? transaction.category
    : transaction?.categoria;
  const validStatus = (type === FIRESTORE_TRANSACTION_TYPES.INCOME
    && [FIRESTORE_TRANSACTION_STATUS.PENDING, FIRESTORE_TRANSACTION_STATUS.RECEIVED].includes(status))
    || (type === FIRESTORE_TRANSACTION_TYPES.EXPENSE
      && [FIRESTORE_TRANSACTION_STATUS.PENDING, FIRESTORE_TRANSACTION_STATUS.PAID].includes(status));
  const valid = typeof id === 'string' && id.length > 0
    && Object.values(FIRESTORE_TRANSACTION_TYPES).includes(type)
    && validStatus
    && Number.isSafeInteger(amountCents) && amountCents >= 0
    && isValidCivilDate(date)
    && yearMonth === date?.slice(0, 7)
    && typeof name === 'string' && name.length <= 160
    && typeof category === 'string' && category.length <= 80
    && transaction?.schemaVersion === FIRESTORE_FINANCIAL_SCHEMA_VERSION;
  if (!valid) fail(FINANCIAL_OCCURRENCE_ERROR_CODES.INVALID_TRANSACTION, [index, id || null]);
  return {
    id, type, status, amountCents, date, yearMonth, name, category,
    createdByUid: transaction.createdByUid ?? null,
    attributedToUid: transaction.attributedToUid ?? null,
    attributedToNameSnapshot: transaction.attributedToNameSnapshot ?? null,
    sharing: transaction.sharing ?? null,
  };
};

const parseSegment = (segment, index) => {
  const { id, ...data } = segment || {};
  try {
    return fromFirestoreRecurringSegment(id, data);
  } catch (error) {
    fail(FINANCIAL_OCCURRENCE_ERROR_CODES.INVALID_SEGMENT, [index, id || null], error);
  }
  return null;
};

const parseState = (state, index) => {
  const { id = state?.occurrenceKey, ...data } = state || {};
  try {
    return fromFirestoreRecurringOccurrenceState(id, data);
  } catch (error) {
    fail(FINANCIAL_OCCURRENCE_ERROR_CODES.INVALID_STATE, [index, id || null], error);
  }
  return null;
};

const toTransactionOccurrence = (transaction, state = null) => ({
  id: transaction.id,
  source: FINANCIAL_OCCURRENCE_SOURCE.TRANSACTION,
  ...(state ? {
    occurrenceKey: state.occurrenceKey,
    seriesId: state.seriesId,
    segmentId: state.segmentId,
  } : {}),
  type: transaction.type,
  status: transaction.status,
  amountCents: transaction.amountCents,
  name: transaction.name,
  category: transaction.category,
  date: transaction.date,
  yearMonth: transaction.yearMonth,
  isVirtual: false,
  transactionId: transaction.id,
  createdByUid: transaction.createdByUid,
  attributedToUid: transaction.attributedToUid,
  attributedToNameSnapshot: transaction.attributedToNameSnapshot,
  sharing: transaction.sharing,
});

const toVirtualOccurrence = (segment, occurrence) => ({
  id: occurrence.occurrenceKey,
  source: FINANCIAL_OCCURRENCE_SOURCE.RECURRING,
  occurrenceKey: occurrence.occurrenceKey,
  seriesId: segment.seriesId,
  segmentId: segment.id,
  type: segment.template.type,
  status: FIRESTORE_TRANSACTION_STATUS.PENDING,
  amountCents: segment.template.amountCents,
  name: segment.template.name,
  category: segment.template.category,
  date: occurrence.scheduledDate,
  yearMonth: occurrence.scheduledDate.slice(0, 7),
  isVirtual: true,
  ...(segment.template.attributedToUid ? { attributedToUid: segment.template.attributedToUid } : {}),
  ...(segment.template.attributedToNameSnapshot ? { attributedToNameSnapshot: segment.template.attributedToNameSnapshot } : {}),
});

export const compareFinancialOccurrences = (left, right) => {
  const dateComparison = compareFinancialRecordsByDate(left, right);
  if (dateComparison !== 0) return dateComparison;
  const leftKey = `${left.source}:${left.id}`;
  const rightKey = `${right.source}:${right.id}`;
  if (leftKey === rightKey) return 0;
  return leftKey < rightKey ? -1 : 1;
};

export const resolveFinancialOccurrences = ({
  transactions = [],
  recurringSegments = [],
  occurrenceStates = [],
  startDate,
  endDate,
} = {}) => {
  if (![transactions, recurringSegments, occurrenceStates].every(Array.isArray)) {
    fail(FINANCIAL_OCCURRENCE_ERROR_CODES.INVALID_INPUT);
  }
  if (!isValidCivilDate(startDate) || !isValidCivilDate(endDate) || startDate > endDate) {
    fail(FINANCIAL_OCCURRENCE_ERROR_CODES.INVALID_RANGE);
  }

  const transactionsById = new Map();
  transactions.forEach((transaction, index) => {
    const normalized = normalizePersistedTransaction(transaction, index);
    if (transactionsById.has(normalized.id)) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.DUPLICATE_TRANSACTION, [normalized.id]);
    }
    transactionsById.set(normalized.id, normalized);
  });

  const segmentsById = new Map();
  const virtualByKey = new Map();
  recurringSegments.forEach((input, index) => {
    const segment = parseSegment(input, index);
    if (segmentsById.has(segment.id)) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.INVALID_SEGMENT, [index, segment.id]);
    }
    segmentsById.set(segment.id, segment);
    if (segment.status !== RECURRING_SEGMENT_STATUS.ACTIVE) return;
    generateRecurrenceOccurrences(toDomainRecurrenceRule(segment), startDate, endDate)
      .forEach((occurrence) => {
        if (virtualByKey.has(occurrence.occurrenceKey)) {
          fail(FINANCIAL_OCCURRENCE_ERROR_CODES.DUPLICATE_VIRTUAL, [occurrence.occurrenceKey]);
        }
        virtualByKey.set(occurrence.occurrenceKey, toVirtualOccurrence(segment, occurrence));
      });
  });

  const statesByKey = new Map();
  const materializedByTransactionId = new Map();
  occurrenceStates.forEach((input, index) => {
    const state = parseState(input, index);
    if (statesByKey.has(state.occurrenceKey)) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.DUPLICATE_STATE, [state.occurrenceKey]);
    }
    statesByKey.set(state.occurrenceKey, state);
    const segment = segmentsById.get(state.segmentId);
    if (!segment) fail(FINANCIAL_OCCURRENCE_ERROR_CODES.STATE_SEGMENT_MISSING, [state.occurrenceKey]);
    if (segment.seriesId !== state.seriesId) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.STATE_SEGMENT_MISMATCH, [state.occurrenceKey]);
    }
    const rule = toDomainRecurrenceRule(segment);
    const scheduledOccurrence = generateRecurrenceOccurrences(
      rule,
      state.scheduledDate,
      state.scheduledDate
    );
    const scheduledMatches = scheduledOccurrence.length === 1
      && scheduledOccurrence[0].occurrenceKey === state.occurrenceKey;
    const effectiveMatches = state.state === RECURRING_OCCURRENCE_STATE.MATERIALIZED
      && generateRecurrenceOccurrences(rule, state.effectiveDate, state.effectiveDate).length === 1;
    if (!scheduledMatches && !effectiveMatches) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.STATE_NOT_IN_SCHEDULE, [state.occurrenceKey]);
    }
    virtualByKey.delete(state.occurrenceKey);

    if (state.state === RECURRING_OCCURRENCE_STATE.SKIPPED) {
      if (transactionsById.has(`rec_${state.occurrenceKey}`)) {
        fail(FINANCIAL_OCCURRENCE_ERROR_CODES.SKIPPED_TRANSACTION_PRESENT, [state.occurrenceKey]);
      }
      return;
    }

    const transaction = transactionsById.get(state.transactionId);
    if (!transaction) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.MATERIALIZED_TRANSACTION_MISSING, [state.occurrenceKey]);
    }
    if (transaction.date !== state.effectiveDate) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.MATERIALIZED_TRANSACTION_MISMATCH, [state.occurrenceKey]);
    }
    if (materializedByTransactionId.has(state.transactionId)) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.TRANSACTION_LINKED_TWICE, [state.transactionId]);
    }
    materializedByTransactionId.set(state.transactionId, state);
  });

  transactionsById.forEach((transaction) => {
    if (
      /^rec_[A-Za-z0-9_-]+_[0-9]{8}$/.test(transaction.id)
      && !materializedByTransactionId.has(transaction.id)
    ) {
      fail(FINANCIAL_OCCURRENCE_ERROR_CODES.MATERIALIZED_STATE_MISSING, [transaction.id]);
    }
  });

  const resolved = [];
  transactionsById.forEach((transaction) => {
    if (transaction.date < startDate || transaction.date > endDate) return;
    resolved.push(toTransactionOccurrence(
      transaction,
      materializedByTransactionId.get(transaction.id) || null
    ));
  });
  resolved.push(...virtualByKey.values());
  return resolved.sort(compareFinancialOccurrences);
};
